import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAppSelector } from '@verantos-dev/core-annotation-tool';

import {
  useGetAllocationSummaryQuery,
  useGetDataSplitSummaryQuery,
} from '../../api/createProjectApiSlice';
import {
  type AgentAllocationPreviewStatus,
  useAnnotationAgentAllocationPreview,
  useIsAnnotationAgentOn,
} from '../../hooks/useAnnotationAgentAllocation';
import { AddedConcept, AnnotationUser } from '../../state/createProjectSlice';
import {
  selectConditionArea,
  selectProjectName,
  selectHealthSystem,
  selectSelectedAnnotators,
  selectSelectedSupervisors,
  selectAddedConcepts,
  selectWorkflowId,
  selectDefaultAllocationMode,
  selectHumanSharePercentages,
} from '../../state/selector';
import {
  AllocationSummaryResponse,
  AnnotationAgentAllocationPreviewResponse,
} from '../../types/response';

// TODO: cleanup with cp_annotation_createproject_agent ff
interface ReviewAgentAllocation {
  status: AgentAllocationPreviewStatus;
  preview: AnnotationAgentAllocationPreviewResponse | null;
  /** The human annotators in selection order, to name the preview's humans. */
  annotators: AnnotationUser[];
  /** Custom mode: each human's row shows their share with their encounters. */
  isCustom: boolean;
}

interface UseReviewAndCreateResults {
  projectName: string;
  conditionAreaName: string;
  healthSystemName: string;
  annotatorNames: string[];
  supervisorNames: string[];
  addedConcepts: AddedConcept[];
  validationEncounterCount: number;
  testEncounterCount: number;
  trainingEncounterCount: number;
  allocationSummary: AllocationSummaryResponse[];
  isAllocationSummaryLoading: boolean;
  isDataSplitSummaryLoading: boolean;
  // TODO: cleanup with cp_annotation_createproject_agent ff
  /** The allocation preview while the annotation agent is on; null while it is off. */
  agentAllocation: ReviewAgentAllocation | null;
}
export const useReviewAndCreate = (): UseReviewAndCreateResults => {
  const { formatMessage } = useIntl();
  const projectName = useAppSelector(selectProjectName);
  const conditionArea = useAppSelector(selectConditionArea);
  const healthSystem = useAppSelector(selectHealthSystem);
  const selectedAnnotators = useAppSelector(selectSelectedAnnotators);
  const selectedSupervisors = useAppSelector(selectSelectedSupervisors);
  const addedConcepts = useAppSelector(selectAddedConcepts);
  const workflowId = useAppSelector(selectWorkflowId);
  const defaultAllocationMode = useAppSelector(selectDefaultAllocationMode);
  // TODO: cleanup with cp_annotation_createproject_agent ff
  const isAgentOn = useIsAnnotationAgentOn();
  const humanSharePercentages = useAppSelector(selectHumanSharePercentages);
  const {
    data: dataSplitSummaryData,
    isLoading: isDataSplitSummaryLoading,
    isFetching: isDataSplitSummaryFetching,
  } = useGetDataSplitSummaryQuery({ workflowId: workflowId ?? '' });
  const dataSplitSummary = useMemo(
    () => dataSplitSummaryData?.data ?? { trainingCount: 0, testCount: 0, validationCount: 0 },
    [dataSplitSummaryData]
  );

  const validationEncounterCount = dataSplitSummary.validationCount;
  const testEncounterCount = dataSplitSummary.testCount;
  const trainingEncounterCount = dataSplitSummary.trainingCount;

  // With the agent on, the allocation comes from the allocation preview instead.
  const {
    data: allocationSummaryData,
    isLoading: isAllocationSummaryLoading,
    isFetching: isAllocationSummaryFetching,
  } = useGetAllocationSummaryQuery(
    {
      selectedAnnotators,
      defaultAllocationMode,
      workflowId: workflowId ?? '',
    },
    { skip: isAgentOn }
  );
  const allocationSummary = useMemo(
    () => allocationSummaryData?.data ?? [],
    [allocationSummaryData]
  );

  // TODO: cleanup with cp_annotation_createproject_agent ff
  // Step 6 keeps Next disabled until every Custom share is a valid whole number.
  const { status: agentPreviewStatus, preview: agentPreview } = useAnnotationAgentAllocationPreview(
    isAgentOn,
    defaultAllocationMode ? 'default' : 'custom',
    selectedAnnotators.map(({ id }) =>
      defaultAllocationMode ? { id } : { id, sharePercentage: Number(humanSharePercentages[id]) }
    )
  );
  const annotatorNames = selectedAnnotators.map((annotator) => annotator.label);

  return {
    projectName,
    conditionAreaName: conditionArea?.label ?? '',
    healthSystemName: healthSystem?.label ?? '',
    annotatorNames: isAgentOn
      ? [
          formatMessage({ id: 'createProjectWizard.step.allocation.agent.label' }),
          ...annotatorNames,
        ]
      : annotatorNames,
    supervisorNames: selectedSupervisors.map((supervisor) => supervisor.label),
    addedConcepts,
    validationEncounterCount,
    testEncounterCount,
    trainingEncounterCount,
    allocationSummary,
    isDataSplitSummaryLoading: isDataSplitSummaryLoading || isDataSplitSummaryFetching,
    isAllocationSummaryLoading: isAllocationSummaryLoading || isAllocationSummaryFetching,
    agentAllocation: isAgentOn
      ? {
          status: agentPreviewStatus,
          preview: agentPreview,
          annotators: selectedAnnotators,
          isCustom: !defaultAllocationMode,
        }
      : null,
  };
};
